'use client';
import type { Language } from '@chargebee/code-sample-generator/browser';
import { CodeSamples } from '@chargebee/code-sample-generator/browser';
import CopyButton from '@components/_components/ui/code-generator/CopyButton';
import SDKSelector from '@components/_components/ui/SDKSelector';
import { PlayIcon } from '@radix-ui/react-icons';
import { Loader } from 'lucide-react';
import React, {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import useSWRMutation from 'swr/mutation';
import { twMerge } from 'tailwind-merge';
import { ClientAuthContext } from '@/auth/ClientAuthProvider';
import { config } from '@/config';
import { useExplorer } from '@/hooks/useExplorer';
import { useTextSelectionTracking } from '@/hooks/useTextSelectionTracking';
import { explorerAnalytics } from '@/lib/analytics';
import { pendoTrack } from '@/lib/pendo/trackPendoEvent';
import { fetcher } from '@/services/Api';
import {
  generateCodeSample,
  prefetchOperationSpec,
} from '@/services/codeGen/adapter';
import { RequestSampleOperation } from '@/services/codeGen/requestSample/Operation';
import type { CodeSample } from '@/services/postmanCollection/postman_util';
// Import cURL syntax highlighting CSS
// Using standalone version to avoid Tailwind directive conflicts
import '@/styles/curl-highlight.css';
// Import line numbers CSS (standalone version to avoid Tailwind conflicts)
import '@/styles/line-numbers.css';
// Import code-samples override styles for clean appearance
import '@/styles/code-samples-override.css';

// One representative language per unique Shiki grammar, used to pre-warm the highlighter.
// curl uses custom highlighting (no Shiki), so it's excluded.
const WARMUP_LANGUAGES: Language[] = [
  'node-v3', // javascript
  'python-v3', // python
  'php-v4', // php
  'ruby', // ruby
  'java-v3', // java
  'dotnet', // csharp
  'go', // go
] as Language[];

/**
 * Pre-loads Shiki grammars for all languages during browser idle time.
 * Defers rendering until the main thread is free so it doesn't block
 * the initial page paint or user interaction.
 */
const ShikiWarmup = React.memo(() => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const start =
      typeof requestIdleCallback === 'function'
        ? requestIdleCallback
        : (cb: () => void) => setTimeout(cb, 2000);

    const id = start(() => setReady(true));

    return () => {
      if (typeof cancelIdleCallback === 'function') {
        cancelIdleCallback(id as number);
      } else {
        clearTimeout(id as number);
      }
    };
  }, []);

  if (!ready) return null;

  return (
    <div
      aria-hidden
      style={{
        position: 'absolute',
        width: 0,
        height: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      {WARMUP_LANGUAGES.map((lang) => (
        <CodeSamples
          key={lang}
          codeString="// warmup"
          language={lang}
          theme="github-light"
          features={{
            copyButton: false,
            showLanguageSwitcher: false,
            showHeader: false,
            showLineNumbers: false,
            showApiExplorer: false,
          }}
        />
      ))}
    </div>
  );
});
ShikiWarmup.displayName = 'ShikiWarmup';

interface RequestSampleContainerProps {
  codeSampleOperation: string;
  codeSample?: CodeSample;
  setSampleResponseDisclaimerVisibility: Dispatch<SetStateAction<boolean>>;
  defaultSDKLanguage?: string | null;
}

export const RequestSampleContainer: React.FC<RequestSampleContainerProps> = ({
  codeSampleOperation,
  codeSample,
  setSampleResponseDisclaimerVisibility,
}) => {
  const [loading, setLoading] = useState(false);
  const [code, setCode] = React.useState<string>('');
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const {
    endpoint,
    apiKey,
    siteName,
    endpointAttributes,
    setResponsePayload,
    selectedSDK,
    setSelectedSDK,
  } = useExplorer();
  const { isLoggedIn, user } = useContext(ClientAuthContext);
  const { trigger: sendRequest } = useSWRMutation(endpoint?.url, fetcher);
  const domainName = config.cbDomain;

  const codeSampleLanguage = selectedSDK as Language;
  const codeContainerRef = useRef<HTMLDivElement>(null);

  // H2.2 — Track text selection in code area (indicates Copy button discoverability issue)
  useTextSelectionTracking(
    codeContainerRef,
    useCallback(
      ({ selectionLength }) => {
        explorerAnalytics.playgroundRequestSelected({
          resource: endpoint?.name || '',
          endpoint: endpoint?.id || '',
          sdkLanguage: selectedSDK,
          selectionLength,
        });
      },
      [endpoint, selectedSDK]
    )
  );

  // Pre-fetch operation spec when route/operation loads to warm up cache
  // This prevents lag when user starts typing
  useEffect(() => {
    if (codeSampleOperation && endpoint) {
      try {
        const operation: RequestSampleOperation =
          new RequestSampleOperation().fromJson(
            JSON.parse(codeSampleOperation)
          );
        // Pre-fetch spec in background (don't await - fire and forget)
        prefetchOperationSpec(operation.id, domainName, siteName, apiKey).catch(
          () => {
            // Silently fail - prefetch is best effort
          }
        );
      } catch (_error) {
        // Ignore errors during prefetch
      }
    }
  }, [codeSampleOperation, endpoint, domainName, siteName, apiKey]);

  useEffect(() => {
    const fetchCode = async () => {
      if (endpoint && endpointAttributes && codeSampleOperation) {
        setIsGeneratingCode(true);

        try {
          const operation: RequestSampleOperation =
            new RequestSampleOperation().fromJson(
              JSON.parse(codeSampleOperation)
            );

          const codeSnippet = await generateCodeSample(
            operation,
            selectedSDK,
            domainName,
            siteName,
            apiKey,
            endpoint,
            endpointAttributes
          );

          setCode(codeSnippet);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          setCode(`// Error generating code: ${errorMessage}`);
        } finally {
          setIsGeneratingCode(false);
        }
      }
    };
    fetchCode();
  }, [
    selectedSDK,
    endpoint,
    endpointAttributes,
    codeSampleOperation,
    apiKey,
    siteName,
    domainName,
  ]);

  const handleSendRequest = useCallback(async () => {
    if (!endpoint || !endpointAttributes) {
      return;
    }
    // H1.1 — Track API execution by auth state
    explorerAnalytics.apiExecuted({
      resource: endpoint.name || '',
      endpoint: endpoint.id || '',
      httpMethod: endpoint.method || '',
      isLoggedIn: !!isLoggedIn,
      sdkLanguage: selectedSDK,
    });
    pendoTrack('api_request_executed', {
      resource: endpoint.name || '',
      endpoint: endpoint.id || '',
      http_method: endpoint.method || '',
      sdk_language: selectedSDK,
      is_logged_in: !!isLoggedIn,
    });

    try {
      if (isLoggedIn) {
        setLoading(true);
        const data = await sendRequest({
          siteId: siteName || '',
          apiKey: apiKey,
          endpoint: endpoint,
          endpointAttributes: endpointAttributes,
          userEmail: user?.email,
        });
        setResponsePayload(data);
        setLoading(false);
      } else {
        if (!isLoggedIn && codeSample) {
          setLoading(true);
          setTimeout(() => {
            if (codeSample.response != null) {
              setSampleResponseDisclaimerVisibility(true);
              setResponsePayload(JSON.parse(codeSample.response));
              setLoading(false);
            }
          }, 300);
        }
      }
    } catch (e) {
      setLoading(false);

      // H1.2 — Track API errors by endpoint
      const errorMessage = e instanceof Error ? e.message : 'Unknown error';
      explorerAnalytics.apiError({
        resource: endpoint.name || '',
        endpoint: endpoint.id || '',
        httpMethod: endpoint.method || '',
        errorType: 'request_failed',
        errorMessage,
      });
      pendoTrack('api_request_failed', {
        resource: endpoint.name || '',
        endpoint: endpoint.id || '',
        http_method: endpoint.method || '',
        error_type: 'request_failed',
        error_message: errorMessage.slice(0, 200),
      });
    }
  }, [
    apiKey,
    endpoint,
    endpointAttributes,
    isLoggedIn,
    selectedSDK,
    sendRequest,
    setResponsePayload,
    setSampleResponseDisclaimerVisibility,
    siteName,
    codeSample,
    user?.email,
  ]);

  return (
    <div className="rounded-md flex flex-col h-full">
      <ShikiWarmup />
      {/* Header */}
      <div className="flex w-full items-center bg-white p-1 border-b border-gray-200">
        <span className="text-sm font-semibold flex-1">
          <div className="font-semibold p-2 text-xs text-[#012A38] flex gap-1 py-2 items-center justify-between bg-white sticky top-0">
            <span className="uppercase">Request</span>
            <button
              type="button"
              className={twMerge(
                'rounded-2xl ml-4 border border-shade-teal-500/20 bg-[rgba(255,255,255,0.52)] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] font-sora p-4 text-left flex-1 flex items-center justify-center gap-1 text-xs font-bold uppercase h-8 text-white bg-gradient-to-r from-[#90C2C7] via-blue-900 to-[#90C2C7] transition-all duration-300 ease-in-out hover:border-shade-teal-500/40',
                !loading && 'active:scale-95'
              )}
              onClick={handleSendRequest}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader className="size-4 animate-spin" />
                  Fetching Response
                </>
              ) : (
                <>
                  <PlayIcon className="size-4" />
                  Run
                </>
              )}
            </button>
            <SDKSelector
              selectedSDK={selectedSDK}
              onSelectSDK={setSelectedSDK}
              variant="subtle"
            />
            <CopyButton
              content={code}
              className="text-black"
              disabled={!code}
              onCopied={() => {
                explorerAnalytics.playgroundRequestCopied({
                  resource: endpoint?.name || '',
                  endpoint: endpoint?.id || '',
                  sdkLanguage: selectedSDK,
                  codeLength: code.length,
                  isLoggedIn: !!isLoggedIn,
                });
                pendoTrack('code_sample_copied', {
                  resource: endpoint?.name || '',
                  endpoint: endpoint?.id || '',
                  sdk_language: selectedSDK,
                  is_logged_in: !!isLoggedIn,
                });
              }}
            />
          </div>
        </span>
      </div>

      <div
        ref={codeContainerRef}
        className="flex-1 overflow-y-auto bg-white relative"
        data-pendo-mask
      >
        {/* Use CodeBlock component for syntax highlighting */}
        {code ? (
          <div
            className={`relative ${isGeneratingCode ? 'opacity-60' : 'opacity-100'} transition-opacity duration-150`}
          >
            <div className="pr-6 py-4">
              <CodeSamples
                codeString={code}
                language={codeSampleLanguage}
                theme="github-light"
                className="bg-transparent border-0 shadow-none p-0 m-0"
                style={{
                  backgroundColor: 'transparent',
                }}
                features={{
                  copyButton: false, // Disabled - using explorer header copy button instead
                  showLanguageSwitcher: false, // Disabled - using explorer header language picker instead
                  showHeader: false, // Remove header - using explorer header instead
                  showLineNumbers: true, // Enable line numbers in code blocks
                  languageFilter: [codeSampleLanguage],
                  title: 'Request Sample',
                  showApiExplorer: false,
                }}
              />
            </div>
            {/* Subtle loading indicator overlay (doesn't cause layout shift) */}
            {isGeneratingCode && (
              <div className="absolute top-6 right-6 flex items-center gap-1.5 text-xs text-gray-600 bg-white/95 px-3 py-1.5 rounded-md shadow-md border border-gray-200 z-10">
                <Loader className="size-3 animate-spin" />
                <span>Updating...</span>
              </div>
            )}
          </div>
        ) : (
          // Initial loading state
          isGeneratingCode && (
            <div className="flex items-center justify-center h-full min-h-[200px]">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader className="size-4 animate-spin" />
                <span>Generating code sample...</span>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};
